/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Tabs, Tab, Divider, Grid, Typography } from '@material-ui/core';
import { TrackChanges, Compare } from '@material-ui/icons';
import PercentIcon from '@mui/icons-material/Percent';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TabContainer from '../../../../components/TabContainer';
import GroupAdvanceSettings from '../GroupAdvanceSettings';
import GroupAdvanceComparison from '../GroupAdvanceComparison';
import GroupAdvanceDiscounting from '../GroupAdvanceDiscounting';
import ValidateUser from '../../../../components/UserStatus';

const styles = makeStyles(theme => ({
  advanceSettings: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    overflowY: 'auto',
    maxHeight: '600px',
    maxWidth: '600px',
    width: '100%',
    height: '100%',
  },
  tabContainer: { height: '90%', overflowY: 'auto' },
  tabSetting: { marginTop: '5px' },
  tabContainerSettings: { marginTop: '10px' },
  advanceSettingsTab: {
    backgroundColor: '#F4F4F4',
    '& .MuiTabs-flexContainer': {
      alignItems: 'center',
      justifyContent: 'space-evenly',
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: '14px',
      borderBottom: '2px solid #BEBEBE',
      paddingRight: '0px',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      borderBottom: '2px solid #ba68c8',
    },
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
  },
  advanceSettingsTabDark: {
    '& .MuiTabs-flexContainer': {
      alignItems: 'center',
      justifyContent: 'space-evenly',
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: '14px',
      borderBottom: '2px solid #7D7D7D',
      paddingRight: '0px',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      borderBottom: '2px solid #ba68c8',
    },
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
  },
  tabIconsStyle: {
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 19,
    height: 19,
    marginTop: '-2px',
    marginLeft: '-2px',
  },
}));

const getSplitNumberFromString = splitStr => {
  if (parseInt(splitStr, 10)) {
    return parseInt(splitStr, 10);
  }

  const replaced = splitStr.replace(/\D/g, '');
  if (replaced) {
    return parseInt(replaced, 10);
  }

  return null;
};

const CustomIconStyled = ({ icon: IconComponent, text, themeType, classes, isSelected, includeRightDivider }) => {
  return (
    <>
      <Grid
        container
        alignContent="center"
        justifyContent="center"
        style={{ marginRight: '32px', marginTop: '3px', height: '39px' }}
      >
        <Grid item xs={1} style={{ marginRight: '9px', marginTop: '2px' }}>
          <div
            className={classes.tabIconsStyle}
            style={
              // eslint-disable-next-line no-nested-ternary
              isSelected
                ? themeType === 'dark'
                  ? { backgroundColor: '#7D1F8D' }
                  : { backgroundColor: '#9C27B0' }
                : { backgroundColor: '#7D7D7D' }
            }
          >
            <IconComponent style={{ fontSize: 14 }} />
          </div>
        </Grid>
        <Grid item xs={4}>
          <Typography style={{ fontWeight: '400', fontSize: '14px' }}>{text}</Typography>
        </Grid>
      </Grid>
      {includeRightDivider ? <Divider orientation="vertical" flexItem style={{ width: '2px' }} /> : <></>}
    </>
  );
};

CustomIconStyled.propTypes = {
  icon: PropTypes.elementType.isRequired,
  text: PropTypes.string.isRequired,
  themeType: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  includeRightDivider: PropTypes.bool.isRequired,
};

function GroupSettingsModal(props) {
  const {
    setSettingsModalOpen,
    settingsModalOpen,
    index,
    groupedListings,
    marketplaces,
    handleMarketplaceChange,
    handleOV,
    handleGroupCriteriaChanges,
    currentComps,
    handleGroupAttrEdit,
    pricingGroup,
  } = props;

  const [tabIndex, setTabIndex] = useState(0);
  const theme = useTheme();
  const classes = styles();

  const handleMarketModalClose = () => {
    setSettingsModalOpen(false);
  };

  const handleMarketModalSubmit = () => {
    handleMarketModalClose();
  };

  const handleChange = (event, value) => {
    setTabIndex(value);
  };

  /**
   * saveZoneFiltersGroup Save to the group all the comps configuration
   * @param {object} zoneFilters configuration of the zones to be save
   * @param {number} index index position of the current group to edit
   */
  const saveZoneFiltersGroup = (zoneFilters, newGroup) => {
    const { saveZoneFilters } = props;
    saveZoneFilters(zoneFilters, newGroup);
  };

  const handleSetCriteria = () => {
    handleMarketModalClose();
  };

  const percentIconStyled = () => {
    const includeRightDivider = false;
    return (
      <CustomIconStyled
        icon={PercentIcon}
        text="Discounting"
        themeType={theme.palette.type}
        classes={classes}
        isSelected={tabIndex === 2}
        includeRightDivider={includeRightDivider}
      />
    );
  };
  const trackIconStyled = () => {
    const includeRightDivider = true;
    return (
      <CustomIconStyled
        icon={TrackChanges}
        text="Broadcasting"
        themeType={theme.palette.type}
        classes={classes}
        isSelected={tabIndex === 0}
        includeRightDivider={includeRightDivider}
      />
    );
  };

  const compareIconStyled = () => {
    return (
      <>
        <Grid
          container
          alignContent="center"
          justifyContent="center"
          style={{ marginLeft: '3px', marginTop: '3px', height: '39px' }}
        >
          <Grid item xs={1} style={{ marginRight: '-14  px', marginTop: '2px' }}>
            <div
              className={classes.tabIconsStyle}
              style={
                // eslint-disable-next-line no-nested-ternary
                tabIndex === 1
                  ? theme.palette.type === 'dark'
                    ? { backgroundColor: '#7D1F8D' }
                    : { backgroundColor: '#9C27B0' }
                  : { backgroundColor: '#7D7D7D' }
              }
            >
              <Compare style={{ fontSize: 14 }} />
            </div>
          </Grid>
          <Grid item xs={10}>
            <Typography style={{ fontWeight: '400', fontSize: '14px' }}> Advanced Comparisons</Typography>
          </Grid>
        </Grid>
        <Divider orientation="vertical" flexItem style={{ width: '2px' }} />
      </>
    );
  };

  return (
    <Dialog
      onClose={handleMarketModalClose}
      aria-labelledby="customized-dialog-title"
      open={settingsModalOpen}
      maxWidth="md"
    >
      <Tabs
        value={tabIndex}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleChange}
        variant="fullWidth"
        TabIndicatorProps={{
          style: { display: 'none' },
        }}
        className={theme.palette.type === 'light' ? classes.advanceSettingsTab : classes.advanceSettingsTabDark}
      >
        <Tab icon={trackIconStyled()} />
        <Tab icon={compareIconStyled()} />
        <Tab icon={percentIconStyled()} />
      </Tabs>

      {tabIndex === 0 && (
        <TabContainer>
          <ValidateUser />
          <GroupAdvanceSettings
            group={pricingGroup}
            handleMarketModalSubmit={handleMarketModalSubmit}
            marketplaces={marketplaces}
            handleMarketplaceChange={handleMarketplaceChange}
            handleGroupAttrEdit={handleGroupAttrEdit}
            getSplitNumberFromString={getSplitNumberFromString}
            handleMarketModalClose={handleMarketModalClose}
            handleSetCriteria={handleSetCriteria}
            groupedListings={groupedListings}
            index={index}
          />
        </TabContainer>
      )}
      {tabIndex === 1 && (
        <TabContainer className={classes.tabContainerSettings}>
          <GroupAdvanceComparison
            comparisonsZones={currentComps}
            isOV={pricingGroup.exclude_obstructed_view === 1}
            handleOV={handleOV}
            handleMarketModalClose={handleMarketModalClose}
            pricingGroup={pricingGroup}
            saveZoneFilters={saveZoneFiltersGroup}
            index={index}
            handleGroupCriteriaChanges={handleGroupCriteriaChanges}
          />
        </TabContainer>
      )}
      {tabIndex === 2 && (
        <GroupAdvanceDiscounting
          handleMarketModalClose={handleMarketModalClose}
          handleGroupAttrEdit={handleGroupAttrEdit}
          handleGroupCriteriaChanges={handleGroupCriteriaChanges}
          pricingGroup={pricingGroup}
        />
      )}
    </Dialog>
  );
}

GroupSettingsModal.propTypes = {
  settingsModalOpen: PropTypes.bool.isRequired,
  setSettingsModalOpen: PropTypes.func.isRequired,
  pricingGroup: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  groupedListings: PropTypes.array.isRequired,
  marketplaces: PropTypes.object.isRequired,
  handleMarketplaceChange: PropTypes.func.isRequired,
  handleOV: PropTypes.func.isRequired,
  saveZoneFilters: PropTypes.func.isRequired,
  handleGroupCriteriaChanges: PropTypes.func.isRequired,
  currentComps: PropTypes.array.isRequired,
  handleGroupAttrEdit: PropTypes.func.isRequired,
};

export default GroupSettingsModal;
